import React, { createContext, useContext, useState } from 'react';

const LanguageContext = createContext();

export const translations = {
  en: {
    nav: {
      home: 'Home',
      about: 'About Me',
      exhibition: 'Exhibition',
      gallery: 'Gallery',
      contact: 'Contact',
    },
    hero: {
      name: 'Veronika Levy',
      tagline: 'Painter · Educator',
      cta: 'View Gallery',
    },
    exhibition: {
      label: 'Exhibitions',
    },
    about: {
      title: 'About Me',
      body: [
        'I am a Czech artist and educator whose work explores memory, identity, and the quiet emotional experiences that shape our lives. Through painting, I explore moments that often remain unspoken: solitude, longing, resilience, and the search for belonging.',
        'I studied Literature and Gender Studies at the Faculty of Arts, Charles University in Prague, where my research focused on literary representations of women\'s Holocaust experiences. This engagement with personal and collective memory continues to inform both my artistic practice and my approach to education.',
        'Many of my paintings feature solitary figures, often women or children, situated in transitional spaces such as roads, windows, or doorways. These settings evoke journeys both physical and emotional, inviting reflection on change, uncertainty, and self-discovery. Rather than telling a fixed story, I hope my work creates space for viewers to bring their own memories and interpretations.',
        'Working with muted palettes and intimate compositions, I seek to capture emotional truths that resist easy definition. Each painting is an attempt to preserve a fleeting feeling, a fragment of memory, or a moment of quiet contemplation. Through both my art and teaching, I aim to encourage meaningful engagement with creativity, reflection, and the human experience.',
      ],
    },
    gallery: {
      title: 'Gallery',
      loading: 'Loading works…',
      error: 'Could not load gallery.',
    },
    contact: {
      title: 'Contact',
      intro: 'Thank you for visiting. I would love to hear from you.',
      email: 'weronikalevy@gmail.com',
      phone: '735 855 207',
    },
    footer: {
      rights: '© 2026 Veronika Levy. All rights reserved.',
    },
  },
  cs: {
    nav: {
      home: 'Úvod',
      about: 'O mně',
      exhibition: 'Výstava',
      gallery: 'Galerie',
      contact: 'Kontakt',
    },
    hero: {
      name: 'Veronika Levy',
      tagline: 'Malířka · Pedagožka',
      cta: 'Zobrazit galerii',
    },
    exhibition: {
      label: 'Výstavy',
    },
    about: {
      title: 'O mně',
      body: [
        'Děkuji, že jste navštívili mou stránku.',
        'Jsem česká umělkyně a pedagožka, jejíž tvorba zkoumá paměť, identitu a tiché emocionální zkušenosti, které formují naše životy. Prostřednictvím malby se věnuji okamžikům, které často zůstávají nevyslovené: samotě, touze, odolnosti a hledání sounáležitosti.',
        'Studovala jsem literaturu a genderová studia na Filozofické fakultě Univerzity Karlovy v Praze, kde se můj výzkum zaměřoval na literární reprezentace ženských zkušeností s holokaustem. Toto propojení s osobní i kolektivní pamětí nadále ovlivňuje jak mou uměleckou tvorbu, tak můj přístup ke vzdělávání.',
        'Mnohé z mých obrazů zobrazují osamělé postavy, často ženy nebo děti, zasazené do přechodových prostorů, jako jsou cesty, okna nebo dveře. Tato prostředí evokují cesty fyzické i emocionální a vybízejí k zamyšlení nad změnou, nejistotou a sebepoznáním. Namísto vyprávění jednoznačného příběhu si přeji, aby má tvorba vytvářela prostor, v němž si diváci mohou promítnout vlastní vzpomínky a interpretace.',
        'Pracuji s tlumenou barevností a intimními kompozicemi a snažím se zachytit emocionální pravdy, které se vzpírají jednoduchému pojmenování. Každý obraz je pokusem uchovat pomíjivý pocit, útržek vzpomínky nebo okamžik tichého rozjímání. Prostřednictvím své tvorby i pedagogické práce usiluji o podporu smysluplného vztahu ke kreativitě, reflexi a lidské zkušenosti.',
      ],
    },
    gallery: {
      title: 'Galerie',
      loading: 'Načítání…',
      error: 'Galerii se nepodařilo načíst.',
    },
    contact: {
      title: 'Kontakt',
      intro: 'Děkuji za návštěvu. Ráda od vás uslyším.',
      email: 'weronikalevy@gmail.com',
      phone: '735 855 207',
    },
    footer: {
      rights: '© 2026 Veronika Levy. Všechna práva vyhrazena.',
    },
  },
};

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState('en');
  const t = translations[lang];
  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  return useContext(LanguageContext);
}
